import { useEffect } from 'react';
import { logEvent, setUserProperties } from 'firebase/analytics';
import { analytics } from '../config/firebase';
/**
 * Get referrer information from URL and document
 */
const getReferrerInfo = () => {
    if (typeof window === 'undefined')
        return null;
    const referrer = document.referrer || '';
    const urlParams = new URLSearchParams(window.location.search);
    // Extract UTM parameters
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    const utmTerm = urlParams.get('utm_term');
    const utmContent = urlParams.get('utm_content');
    // Determine source
    let source = 'direct';
    let medium = 'none';
    let sourceDetail = '';
    if (utmSource) {
        source = utmSource;
        medium = utmMedium || 'campaign';
        sourceDetail = utmCampaign || '';
    }
    else if (referrer) {
        try {
            const referrerUrl = new URL(referrer);
            const hostname = referrerUrl.hostname.toLowerCase();
            // Common referrer sources
            if (hostname.includes('google') || hostname.includes('bing') || hostname.includes('yahoo')) {
                source = 'search_engine';
                medium = 'organic';
                sourceDetail = hostname;
            }
            else if (hostname.includes('linkedin.com')) {
                source = 'linkedin';
                medium = 'social';
                sourceDetail = 'linkedin';
            }
            else if (hostname.includes('github.com')) {
                source = 'github';
                medium = 'social';
                sourceDetail = 'github';
            }
            else if (hostname.includes('twitter.com') || hostname.includes('x.com')) {
                source = 'twitter';
                medium = 'social';
                sourceDetail = 'twitter';
            }
            else if (hostname.includes('facebook.com')) {
                source = 'facebook';
                medium = 'social';
                sourceDetail = 'facebook';
            }
            else if (hostname.includes('youtube.com')) {
                source = 'youtube';
                medium = 'social';
                sourceDetail = 'youtube';
            }
            else {
                source = 'referral';
                medium = 'referral';
                sourceDetail = hostname;
            }
        }
        catch {
            source = 'referral';
            medium = 'referral';
            sourceDetail = referrer;
        }
    }
    return {
        referrer,
        source,
        medium,
        sourceDetail,
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        utmContent,
    };
};
/**
 * Custom hook for Firebase Analytics tracking
 * Safe for SSG - only runs on client side
 */
export const useAnalytics = () => {
    // Track page view
    const trackPageView = (pageName, pageTitle) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        const referrerInfo = getReferrerInfo();
        logEvent(analytics, 'page_view', {
            page_title: pageTitle || pageName,
            page_location: window.location.href,
            page_path: window.location.pathname,
            ...(referrerInfo && {
                referrer_source: referrerInfo.source,
                referrer_medium: referrerInfo.medium,
            }),
        });
    };
    // Track section view (e.g., when user scrolls to a section)
    const trackSectionView = (sectionName) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        logEvent(analytics, 'view_section', {
            section_name: sectionName,
        });
    };
    // Track button/link click
    const trackClick = (elementName, elementType = 'button', additionalData) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        logEvent(analytics, 'click', {
            element_name: elementName,
            element_type: elementType,
            ...additionalData,
        });
    };
    // Track project view
    const trackProjectView = (projectId, projectTitle) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        logEvent(analytics, 'view_project', {
            project_id: projectId,
            project_title: projectTitle,
        });
    };
    // Track contact form interaction
    const trackContactForm = (action, errorMessage) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        logEvent(analytics, 'contact_form', {
            action,
            ...(errorMessage && { error_message: errorMessage }),
        });
    };
    // Track download action
    const trackDownload = (itemName, itemType = 'file') => {
        if (typeof window === 'undefined' || !analytics)
            return;
        logEvent(analytics, 'file_download', {
            file_name: itemName,
            file_type: itemType,
        });
    };
    // Track external link click
    const trackExternalLink = (linkUrl, linkText) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        logEvent(analytics, 'external_link_click', {
            link_url: linkUrl,
            link_text: linkText,
        });
    };
    // Track scroll depth
    const trackScrollDepth = (depth) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        logEvent(analytics, 'scroll', {
            scroll_depth: depth,
        });
    };
    // Set user properties (optional - for user segmentation)
    const setUserProperty = (propertyName, value) => {
        if (typeof window === 'undefined' || !analytics)
            return;
        setUserProperties(analytics, {
            [propertyName]: value,
        });
    };
    return {
        trackPageView,
        trackSectionView,
        trackClick,
        trackProjectView,
        trackContactForm,
        trackDownload,
        trackExternalLink,
        trackScrollDepth,
        setUserProperty,
    };
};
/**
 * Hook to automatically track page views on route changes
 */
export const usePageTracking = (pageName, pageTitle) => {
    const { trackPageView } = useAnalytics();
    useEffect(() => {
        // Only track on client side
        if (typeof window === 'undefined')
            return;
        const referrerInfo = getReferrerInfo();
        // Track page view with referrer info
        trackPageView(pageName, pageTitle);
        // Log referrer information as a separate event for better tracking
        if (referrerInfo && analytics) {
            logEvent(analytics, 'page_referrer', {
                page_title: pageTitle || pageName,
                referrer_source: referrerInfo.source,
                referrer_medium: referrerInfo.medium,
                referrer_detail: referrerInfo.sourceDetail,
                referrer_url: referrerInfo.referrer || 'direct',
                ...(referrerInfo.utmSource && { utm_source: referrerInfo.utmSource }),
                ...(referrerInfo.utmMedium && { utm_medium: referrerInfo.utmMedium }),
                ...(referrerInfo.utmCampaign && { utm_campaign: referrerInfo.utmCampaign }),
            });
            // Set user properties for segmentation
            setUserProperties(analytics, {
                first_referrer_source: referrerInfo.source,
                first_referrer_medium: referrerInfo.medium,
            });
        }
    }, [pageName, pageTitle, trackPageView]);
};
/**
 * Hook to track scroll depth
 */
export const useScrollTracking = () => {
    const { trackScrollDepth } = useAnalytics();
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const trackedDepths = new Set();
        const depths = [25, 50, 75, 90, 100]; // Track at these percentages
        const handleScroll = () => {
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollPercentage = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);
            depths.forEach((depth) => {
                if (scrollPercentage >= depth && !trackedDepths.has(depth)) {
                    trackedDepths.add(depth);
                    trackScrollDepth(depth);
                }
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [trackScrollDepth]);
};
